// InvoicePDF.js

import React from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Helper function to replace problematic characters
const replaceProblematicChars = (text) => {
  if (!text) return "";
  return text
    .replace(/ć/g, "c")
    .replace(/č/g, "c")
    .replace(/Ć/g, "C")
    .replace(/Č/g, "C")
    .replace(/đ/g, "dj")
    .replace(/Đ/g, "Dj")
    .replace(/ž/g, "z")
    .replace(/Ž/g, "Z")
    .replace(/š/g, "s")
    .replace(/Š/g, "S");
};

export const createInvoiceDocument = ({
  indentCode,
  entries,
  indentType,
  customer,
  totalCost,
  totalWeight,
  totalNumberOfPackages,
  totalRealQuantity,
  comment,
}) => {
  // Create PDF document with higher quality settings
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  // Set the font to a standard font
  doc.setFont("helvetica", "normal");

  // Page setup
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 8; // Reduced margin to save space
  const usablePageWidth = pageWidth - margin * 2;

  // Current date
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed, so add 1
  const year = now.getFullYear();
  const currentDate = `${day}-${month}-${year}`;

  // Document title - more compact
  doc.setFontSize(14); // Reduced font size
  doc.setFont("helvetica", "bold");
  doc.text(
    replaceProblematicChars(`PORUDZBINA BR: ${indentCode}`),
    pageWidth / 2,
    15, // Moved up
    { align: "center" }
  );

  // Date on the same line as the title, but right-aligned
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(
    replaceProblematicChars(`Datum: ${currentDate}`),
    pageWidth - margin,
    15,
    { align: "right" }
  );

  // Draw a horizontal line under the header
  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(0.3);
  doc.line(margin, 18, pageWidth - margin, 18);

  // Customer and payment information section - more compact
  let yPosition = 22; // Moved up

  // Customer and payment info in a single row to save space
  doc.setFillColor(245, 245, 245);
  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(0.1);
  doc.roundedRect(margin, yPosition, usablePageWidth, 20, 1, 1, 'FD'); // Smaller height

  // Customer info (left side)
  if (customer) {
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("KUPAC:", margin + 3, yPosition + 5);

    doc.setFont("helvetica", "normal");
    const customerName = replaceProblematicChars(customer.nameOfTheLegalEntity);
    const contactInfo = replaceProblematicChars(`${customer.contactPerson || ""}${customer.contactPerson ? ', ' : ''}${customer.address || ""}, ${customer.city || ""}`);

    doc.text(customerName, margin + 25, yPosition + 5);
    doc.text(contactInfo, margin + 25, yPosition + 12);
  }

  // Payment info (right side)
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("NACIN PLACANJA:", pageWidth / 2 + 5, yPosition + 5);

  doc.setFont("helvetica", "normal");
  doc.text(
    replaceProblematicChars(
      indentType === 'GOTOVINSKI_RACUN'
        ? 'GOTOVINSKI RACUN'
        : indentType?.replace(/_/g, ' ') || "Nije dostupno"
    ),
    pageWidth / 2 + 40,
    yPosition + 5
  );

  // Table section title - more compact
  yPosition = 46; // Moved up
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("STAVKE PORUDZBINE:", margin, yPosition);

  // Table configuration
  const tableColumns = [
    { header: "#", dataKey: "index" },
    { header: "Sifra proizvoda", dataKey: "code" },
    { header: "Artikal", dataKey: "article" },
    { header: "Kolicina", dataKey: "quantity" }, // Shortened
    { header: "Realna Tezina", dataKey: "actualWeight" },
  ];

  // Prepare table data
  const tableData = entries.map((entry, index) => ({
    index: (index + 1).toString(),
    code: entry.article.code || "-",
    article: replaceProblematicChars(entry.article.name),
    unit: replaceProblematicChars(entry.article.unitOfMeasurement),
    quantity: entry.requestedQuantity.toString(),
    // Warehouse staff enter the measured weight by hand on the printed sheet.
    actualWeight: "",
  }));

  // Generate table with more compact styling
  autoTable(doc, {
    startY: yPosition + 3,
    head: [tableColumns.map(col => col.header)],
    body: tableData.map(row => tableColumns.map(col => row[col.dataKey])),
    theme: 'grid',
    styles: {
      fontSize: 8, // Smaller font
      cellPadding: 2, // Reduced padding
      lineColor: [80, 80, 80],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [220, 220, 220],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      lineColor: [50, 50, 50],
      lineWidth: 0.2,
      halign: 'center',
      valign: 'middle',
      cellPadding: 2, // Reduced padding
      minCellHeight: 10, // Smaller header height
    },
    bodyStyles: {
      fontSize: 11,
      lineColor: [180, 180, 180],
      minCellHeight: 8, // Smaller row height
    },
    alternateRowStyles: {
      fillColor: [240, 240, 240],
    },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' }, // Narrower
      1: { cellWidth: 30, halign: 'left', overflow: 'linebreak' },
      2: { cellWidth: 'auto', overflow: 'linebreak' },
      3: { cellWidth: 18, halign: 'center' },
      4: { cellWidth: 30, halign: 'center' },
    },
    margin: { left: margin, right: margin },
    didDrawPage: (data) => {
      // Add page number
      const pageNumber = doc.internal.getNumberOfPages();
      doc.setFontSize(7);
      doc.text(
        `Strana ${pageNumber}`,
        pageWidth - margin,
        pageHeight - 5,
        { align: 'right' }
      );

      // Add header to each page if multiple pages
      if (pageNumber > 1) {
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(
          replaceProblematicChars(`PORUDZBINA BR: ${indentCode}`),
          pageWidth / 2,
          10,
          { align: "center" }
        );
        doc.line(margin, 12, pageWidth - margin, 12);
      }
    },
    didParseCell: function (data) {
      // Add extra styling to cells
      if (data.section === 'head') {
        data.cell.styles.fillColor = [220, 220, 220];
        data.cell.styles.textColor = [0, 0, 0];
        data.cell.styles.fontStyle = 'bold';
      }

      // Bold the first column
      if (data.column.index === 0 && data.section === 'body') {
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  // Get the final Y position after the table
  const finalY = doc.lastAutoTable.finalY + 5; // Reduced spacing

  // Add comment section - more compact
  if (comment && comment.trim()) {
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("NAPOMENA:", margin, finalY);

  // Draw comment box with border
  doc.setDrawColor(100, 100, 100);
  doc.setFillColor(245, 245, 245);
  doc.setLineWidth(0.1);

  // Calculate available height for comment box
  const availableHeight = Math.min(25, pageHeight - finalY - 15);
  doc.roundedRect(margin, finalY + 2, usablePageWidth, availableHeight, 1, 1, 'FD');

  // Add comment text
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  const commentText = replaceProblematicChars(comment);
  const splitComment = doc.splitTextToSize(
    commentText,
    usablePageWidth - 6
  );
  doc.text(splitComment, margin + 3, finalY + 7);
  }
  return doc;
};

const InvoicePDF = (props) => {
  const handlePrint = () => {
    createInvoiceDocument(props).save(`Porudzbina_${props.indentCode}.pdf`);
  };

  return (
    <button
      onClick={handlePrint}
      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
    >
      <i className="fas fa-print mr-2"></i>
      Stampaj
    </button>
  );
};

export default InvoicePDF;
